import { Box, Text, VStack } from '@chakra-ui/react';
import {
  BackofficeDetailPromoItemWithDiscount,
  BackofficeDetailPromoItemWithFreeGoods,
  GroupedPromoItem,
} from '@diamond/shared/types';
import { adminCurrencyFormatter } from '@diamond/shared/utils';

import { isDiscount, isFreeGoods } from '../../utils';

type DetailsMechanismeProps = {
  data: GroupedPromoItem[];
};

// Constant to convert from backend magnified units to display units
const STOCK_MAGNIFIER = 1000;

/**
 * DetailsMechanisme Component
 * Displays promo mechanism details in a readable format based on promo type
 * Handles two main types of promotions:
 * 1. Free Goods - Products given free with purchase
 * 2. Discounts - Price reductions (percentage or fixed amount)
 */
export function DetailsMechanisme({ data }: DetailsMechanismeProps) {
  if (!data || data.length === 0) {
    return null;
  }

  if (isFreeGoods(data)) {
    return <FreeGoodsMechanism data={data} />;
  }

  if (isDiscount(data)) {
    return <DiscountMechanism data={data} />;
  }

  return null;
}

/**
 * FreeGoodsMechanism Component
 * Displays free goods promotion details
 * Can handle both regular and tiered free goods promotions
 *
 * @param data - Array of promotion items containing free goods details
 */
function FreeGoodsMechanism({
  data,
}: {
  data: BackofficeDetailPromoItemWithFreeGoods[];
}) {
  const firstItem = data[0];
  const freeGood = firstItem.product_free_good;
  const unitDesc = firstItem.product.sales_unit_desc;

  // Check if this is a tiered promotion (multiple levels of free goods)
  const isTiered = freeGood.sequential_number_for_free_goods_scale > 0;

  return (
    <Box mt={2}>
      <VStack align="flex-start" spacing={2}>
        {/* Requirements Section */}
        <Text fontWeight="medium">Syarat:</Text>
        {isTiered ? (
          // Simplified tier display without product details
          <Text ml={4} fontWeight="bold">
            - Beli {freeGood.minimum_order_quantity / STOCK_MAGNIFIER}{' '}
            {unitDesc}
          </Text>
        ) : (
          // Original detailed display for single items
          renderRequirements(
            { '1': data },
            (item: BackofficeDetailPromoItemWithFreeGoods, product) => {
              const freeGood = item.product_free_good;
              return formatPurchaseRequirement(
                freeGood.sum_minimum_order_quantity,
                freeGood.sum_minimum_order_amount,
                freeGood.minimum_order_quantity,
                freeGood.minimum_order_amount,
                product.sales_unit_desc
              );
            }
          )
        )}

        {/* Benefits Section */}
        <Text fontWeight="medium" mt={2}>
          Promo:
        </Text>
        {isTiered ? (
          // Simplified tier display without product details
          <Text ml={4}>
            - Gratis {(freeGood.quantity_free_goods ?? 0) / STOCK_MAGNIFIER}{' '}
            {unitDesc}
          </Text>
        ) : (
          // Original detailed display for single items
          renderBenefits(
            { '1': data },
            (item: BackofficeDetailPromoItemWithFreeGoods) => {
              const freeGood = item.product_free_good;
              return `Gratis ${
                (freeGood.quantity_free_goods ?? 0) / STOCK_MAGNIFIER
              } ${freeGood.sales_unit_desc} ${freeGood.title} (${
                freeGood.free_goods_product_item_code
              })`;
            }
          )
        )}
      </VStack>
    </Box>
  );
}

/**
 * DiscountMechanism Component
 * Displays discount promotion details
 * Can handle both regular and tiered discount promotions
 * Supports both percentage and nominal discounts
 *
 * @param data - Array of promotion items containing discount details
 */
function DiscountMechanism({
  data,
}: {
  data: BackofficeDetailPromoItemWithDiscount[];
}) {
  const firstItem = data[0];
  const discount = firstItem.product_discount;
  const unitDesc = firstItem.product.sales_unit_desc;

  // Check if this is a tiered promotion (multiple discount levels)
  const isTiered = discount.calculation_number > 1;

  return (
    <Box mt={2}>
      <VStack align="flex-start" spacing={2}>
        {/* Requirements Section */}
        <Text fontWeight="medium">Syarat:</Text>
        {isTiered ? (
          // Simplified tier display without product details
          <Text ml={4}>
            - Beli {discount.minimum_order_quantity / STOCK_MAGNIFIER}{' '}
            {unitDesc}
          </Text>
        ) : (
          // Original detailed display for single items
          renderRequirements(
            { '1': data },
            (item: BackofficeDetailPromoItemWithDiscount, product) => {
              const discount = item.product_discount;
              return formatPurchaseRequirement(
                discount.sum_minimum_order_quantity,
                discount.sum_minimum_order_amount,
                discount.minimum_order_quantity,
                discount.minimum_order_amount,
                product.sales_unit_desc
              );
            }
          )
        )}

        {/* Benefits Section */}
        <Text fontWeight="medium" mt={2}>
          Promo:
        </Text>
        {isTiered ? (
          // Simplified tier display without product details
          <Text ml={4} fontWeight="bold">
            -{' '}
            {discount.is_discount
              ? `Diskon ${discount.deduction_amount / 100}%`
              : `Cashback ${adminCurrencyFormatter(discount.deduction_amount)}`}
          </Text>
        ) : (
          // Original detailed display for single items
          renderBenefits(
            { '1': data },
            (item: BackofficeDetailPromoItemWithDiscount) => {
              const discount = item.product_discount;
              if (discount.is_discount) {
                return `Diskon ${discount.deduction_amount / 100}%`;
              } else {
                return `Cashback ${adminCurrencyFormatter(
                  discount.deduction_amount
                )}`;
              }
            }
          )
        )}
      </VStack>
    </Box>
  );
}

type TiersType<T> = {
  [key: string]: T[];
};

/**
 * Renders the requirements section for each tier
 *
 * @param tiers - Grouped promotion items by tier
 * @param formatFn - Function to format the requirement text
 * @returns JSX elements for requirements
 */
function renderRequirements<T extends { product: any }>(
  tiers: TiersType<T>,
  formatFn: (item: T, product: any) => string
) {
  return Object.keys(tiers).map((tier) => {
    const tierItems = tiers[tier];
    const firstItem = tierItems[0];
    const product = firstItem.product;

    const requirement = formatFn(firstItem, product);

    return (
      <Text key={`req-${tier}`} ml={4} fontWeight="bold">
        - {requirement}
      </Text>
    );
  });
}

/**
 * Renders the benefits section for each tier
 *
 * @param tiers - Grouped promotion items by tier
 * @param formatFn - Function to format the benefit text
 * @returns JSX elements for benefits
 */
function renderBenefits<T>(tiers: TiersType<T>, formatFn: (item: T) => string) {
  return Object.keys(tiers).map((tier) => {
    const tierItems = tiers[tier];
    const firstItem = tierItems[0];

    const benefit = formatFn(firstItem);

    return (
      <Text key={`ben-${tier}`} ml={4} fontWeight="bold">
        - {benefit}
      </Text>
    );
  });
}

/**
 * Formats purchase requirement text based on quantity or amount
 *
 * @param sumQuantity - Sum minimum order quantity for bundled promotions
 * @param sumAmount - Sum minimum order amount for bundled promotions
 * @param quantity - Minimum order quantity for single product promotions
 * @param amount - Minimum order amount for single product promotions
 * @param unitDesc - Unit description (e.g., 'Karton', 'Botol')
 * @returns Formatted requirement text
 */
function formatPurchaseRequirement(
  sumQuantity: number,
  sumAmount: number,
  quantity: number,
  amount: number,
  unitDesc: string
): string {
  if (sumQuantity > 0) {
    return `Beli ${sumQuantity / STOCK_MAGNIFIER} ${unitDesc}`;
  }

  if (sumAmount > 0) {
    return `Beli senilai ${adminCurrencyFormatter(sumAmount)}`;
  }

  if (quantity > 0) {
    return `Beli ${quantity / STOCK_MAGNIFIER} ${unitDesc}`;
  }

  return `Beli senilai ${adminCurrencyFormatter(amount)}`;
}

export default DetailsMechanisme;
